'use client';

import React, { forwardRef } from 'react';
import TransitionLink from '@/components/TransitionLink';
import { cva, type VariantProps } from 'class-variance-authority';
import { PiVideoDuotone, PiCaretCircleRightBold } from 'react-icons/pi';
import { HiExternalLink } from 'react-icons/hi';
import { HiMiniArrowSmallRight } from 'react-icons/hi2';
import { FaAngleDown } from 'react-icons/fa';

const buttonVariants = cva(
  'splashbutton ph-convert rounded-md uppercase text-center font-bold ring-1 ring-offset-1 ring-offset-transparent inline-block transition-all hover:scale-[1.02] hover:ring-transparent active:scale-[0.98]',
  {
    variants: {
      color: {
        blue: 'bg-gradient-to-br from-blue-400 to-blue-700 ring-blue-500/50 active:ring-blue-500/70 text-white',
        red: 'bg-gradient-to-br from-red-400 to-red-800 ring-red-500/50 active:ring-red-500/70 text-white',
        yellow:
          'bg-gradient-to-br from-yellow-400 to-yellow-700 ring-yellow-500/50 active:ring-yellow-500/70 text-white',
        white: 'bg-white ring-blue/50 active:ring-blue/70 text-blue hover:bg-white/8',
        transparent: 'bg-white/10 ring-white/50 active:ring-white/70 text-white hover:bg-white/20',
        transparentblue:
          'bg-white/20 ring-blue/60 active:ring-blue/80 text-blue-600 hover:bg-blue/5',
        transparentred: 'bg-transparent ring-red/50 active:ring-red/70 text-red hover:bg-red/5',
        transparentblack:
          'bg-transparent ring-black/50 active:ring-black/70 text-black hover:bg-black/5',
      },
      size: {
        sm: 'px-3 py-1.5 text-xs',
        cm: 'px-[1rem] sm:px-[1.5rem] sm:py-[0.5rem] py-[0.35rem] text-xs relative -top-[2px]',
        md: 'px-4 sm:px-4  text-[15px] h-[44px] inline-flex items-center justify-center font-[800]',
        lg: 'text-sm py-2 px-4 sm:px-6 sm:py-3 sm:text-base h-[44px] inline-flex items-center justify-center',
        xl: 'px-8 py-4 text-lg',
      },
    },
    defaultVariants: {
      color: 'red',
      size: 'md',
    },
  }
);

interface SplashButtonProps extends VariantProps<typeof buttonVariants> {
  url?: string;
  text: string;
  className?: string;
  id?: string;
  grow?: boolean;
  preventNavigation?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  icon?: 'video' | 'external' | 'start' | 'down' | 'arrow';
  as?: 'button';
  disabled?: boolean;
}

const SplashButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, SplashButtonProps>(
  (props, ref) => {
    const {
      url = '/',
      text = 'Sign up',
      color,
      size,
      className = '',
      grow = false,
      preventNavigation = false,
      onClick,
      icon,
      as,
      ...rest
    } = props;

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (preventNavigation) {
        e.preventDefault();
      }
      if (onClick) {
        onClick(e);
      }
    };

    if (as === 'button') {
      return (
        <button
          type="button"
          className={`${buttonVariants({ color, size })} ${grow ? 'w-full' : ''} ${className}`}
          onClick={handleClick}
          ref={ref as React.Ref<HTMLButtonElement>}
          id={props.id}
          {...rest}
        >
          {icon === 'video' && <PiVideoDuotone className="mr-2 inline-block align-middle text-lg" />}
          {text}
          {icon === 'start' && (
            <PiCaretCircleRightBold className="ml-2 inline-block align-middle text-lg" />
          )}
          {icon === 'external' && <HiExternalLink className="ml-2 inline-block align-middle text-lg" />}
          {icon === 'down' && <FaAngleDown className="ml-2 inline-block align-middle text-lg" />}
          {icon === 'arrow' && (
            <HiMiniArrowSmallRight className="ml-1.5 inline-block align-middle text-xl" />
          )}
        </button>
      );
    }

    return (
      <TransitionLink
        href={url}
        className={`${buttonVariants({ color, size })} ${grow ? 'w-full' : ''} ${className}`}
        onClick={handleClick}
        ref={ref as React.Ref<HTMLAnchorElement>}
        id={props.id}
        {...rest}
      >
        {icon === 'video' && <PiVideoDuotone className="mr-2 inline-block align-middle text-lg" />}
        {text}
        {icon === 'start' && (
          <PiCaretCircleRightBold className="ml-2 inline-block align-middle text-lg" />
        )}
        {icon === 'external' && <HiExternalLink className="ml-2 inline-block align-middle text-lg" />}
        {icon === 'down' && <FaAngleDown className="ml-2 inline-block align-middle text-lg" />}
        {icon === 'arrow' && (
          <HiMiniArrowSmallRight className="ml-1.5 inline-block align-middle text-xl" />
        )}
      </TransitionLink>
    );
  }
);

SplashButton.displayName = 'SplashButton';

export default SplashButton;
